import { useMemo } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';

import AutoComplete from 'components/field/AutoComplete';
import EnumSelect from 'components/field/EnumSelect';
import StringInput from 'components/field/StringInput';
import SwitchInput from 'components/field/SwitchInput';
import FormItem from 'components/form/FormItem';
import Table from 'components/table/Table';

import { AUTOCOMPLETE, ENUMS } from 'services/consts';

/**
 *
 * @author @omerrkosar
 */

const SaleChannelInventorySync = ({ mode, id }) => {
	const formMethods = useFormContext();
	const viewMode = mode === 'view';
	const {
		setValue,
		watch,
		control,
		formState: { isSubmitting, isLoading: isFormLoading }
	} = formMethods;

	const isLoading = isFormLoading || isSubmitting;

	const { fields, append, remove } = useFieldArray({ control, name: 'productSalesChannelList' });

	const validateOptions = {
		shouldValidate: true,
		shouldDirty: true
	};

	const saleChannelAutocompleteColumns = useMemo(
		() => [
			{ accessor: 'Name', width: 200 },
			{ accessor: 'ID', width: 200 }
		],
		[]
	);
	const columns = useMemo(
		() => [
			{
				header: 'saleChannelAndID',
				id: 'saleChannelAndID',
				renderCell: ({ row: { index } }) => (
					<AutoComplete
						viewMode={viewMode}
						columns={saleChannelAutocompleteColumns}
						selectionLabelAccessor={['Name']}
						autoCompleteName={AUTOCOMPLETE.SALES_CHANNELS}
						name={`productSalesChannelList[${index}].salesChannelCredentialsID`}
						enumName={ENUMS.PRICE_CONFIGURATION_TIER_TYPE}
						setValue={(value) => {
							setValue(
								`productSalesChannelList[${index}].salesChannelCredentialsID`,
								value?.ID ?? null,
								validateOptions
							);
						}}
						value={watch(`productSalesChannelList[${index}].salesChannelCredentialsID`)}
					/>
				)
			},
			{
				header: 'inventoryCalculateMethod',
				id: 'inventoryCalculateMethod',
				renderCell: ({ row: { index } }) => (
					<EnumSelect
						viewMode={viewMode}
						searchable={false}
						name={`productSalesChannelList[${index}].inventoryCalculateMethod`}
						enumName={ENUMS.INVENTORY_CALCULATE_METHOD}
						setValue={(value) => {
							setValue(
								`productSalesChannelList[${index}].inventoryCalculateMethod`,
								value ?? null,
								validateOptions
							);
						}}
						value={watch(`productSalesChannelList[${index}].inventoryCalculateMethod`)}
					/>
				)
			},

			{
				id: 'saleChannelProductID',
				header: 'saleChannelProductID',
				renderCell: ({ row: { index } }) => (
					<StringInput viewMode={viewMode} name={`productSalesChannelList[${index}].salesChannelProductID`} />
				)
			},
			{
				id: 'inventorySync',
				header: 'inventorySync',
				size: 60,
				renderCell: ({ row: { index } }) => (
					<SwitchInput
						className="mx-auto"
						viewMode={viewMode}
						name={`productSalesChannelList[${index}].inventorySync`}
						style={{ width: 'auto' }}
					/>
				)
			},
			{
				header: 'salesChannelCategoryId',
				id: 'salesChannelCategoryId',
				renderCell: ({ row: { index } }) => (
					<StringInput
						viewMode={viewMode}
						name={`productSalesChannelList[${index}].salesChannelCategoryId`}
					/>
				)
			},
			{
				header: 'salesChannelCategoryName',
				id: 'salesChannelCategoryName',
				renderCell: ({ row: { index } }) => (
					<StringInput
						viewMode={viewMode}
						name={`productSalesChannelList[${index}].salesChannelCategoryName`}
					/>
				)
			},

			...(!viewMode
				? [
						{
							id: 'deleteRow',
							size: 30,
							maxSize: 30
						}
					]
				: [])
		],
		[id]
	);

	return (
		<FormItem>
			<Table
				loading={isLoading}
				data={fields}
				columns={columns}
				onRowDelete={(row) => {
					remove(row.index);
				}}
				onRowAdd={() => {
					append({});
				}}
				showAddNewRowButton={!viewMode}
			/>
		</FormItem>
	);
};

export default SaleChannelInventorySync;
